/**
 * Bullet List Item Component
 * Shared component for displaying items with bullet point, heading, and description
 * Used by: Cultural Aspects, Historical Significance, Navigation Difficulty
 * Follows Single Responsibility Principle - only responsible for rendering bullet list items
 */

import React from 'react';
import {
  spacing,
  typography,
  typographyColors,
  fontPrimary,
} from '@/design-system/utils';
import { Heading } from '@/components/ui/heading';
import { BulletPoint } from '@/components/ui/bullet-point';

export interface BulletListItemData {
  /**
   * Unique identifier for the item
   */
  id?: string;
  /**
   * Heading text (displayed in bold)
   */
  heading: string;
  /**
   * Description text (displayed after heading with colon separator)
   */
  description: string;
}

export interface BulletListItemProps {
  /**
   * Item data with heading and description
   */
  item: BulletListItemData;
  /**
   * Optional custom className
   */
  className?: string;
}

/**
 * Bullet List Item Component
 * Renders a single bullet list item with bold heading and description
 * Matches Figma design: bullet point with bold heading followed by colon and description
 */
export const BulletListItem: React.FC<BulletListItemProps> = ({
  item,
  className,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing[3],
      }}
      className={className}
    >
      {/* Bullet Point Icon */}
      <BulletPoint size={20} marginTop='4px' />
      {/* Content - Heading and Description */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          flex: 1,
        }}
      >
        <div
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.base, // 16px
            fontWeight: typography.weight.regular,
            color: typographyColors.text,
            lineHeight: typography.lineHeight.relaxed,
            margin: 0,
          }}
        >
          {item.heading ? (
            <>
              <Heading
                as='h5'
                fontSize={typography.size.base} // 16px
                fontWeight={typography.weight.semibold} // 600
                color='#363636'
                lineHeight='normal'
                style={{ display: 'inline', margin: 0, padding: 0 }}
              >
                {item.heading}
              </Heading>
              {': '}
              <span>{item.description}</span>
            </>
          ) : (
            <span>{item.description}</span>
          )}
        </div>
      </div>
    </div>
  );
};
